/**
 * MapCanvas — Google Maps JS SDK host (FRE-673 5D).
 *
 * Loading strategy:
 *  - The SDK is pulled in via `@googlemaps/js-api-loader`, which injects a
 *    `<script async>` tag on first `importLibrary()` call. No synchronous
 *    `<script src="maps.googleapis.com">` ever appears in the document — keeps
 *    the main thread free for paint and lets the mobile skeleton below render
 *    before Maps JS executes.
 *  - `importLibrary()` lazily fetches only the library families we reference
 *    (`maps`, `marker`, `places`), so the initial JS payload is smaller than
 *    pulling the full SDK.
 *  - `setOptions()` is gated by `loaderInitialized` so the loader's
 *    "setOptions called after library load" warning never fires even when
 *    multiple `MapCanvas` instances mount (e.g. in tests, or transient
 *    remounts during hydration).
 *  - The `google.maps.Map` instance is preserved in `googleMapRef` across
 *    re-renders; center/zoom/tilt/maxZoom updates flow through a second effect
 *    calling `setCenter`/`setZoom`/`setOptions` rather than recreating the
 *    map — recreation would discard the tile cache and retrigger paid Places
 *    calls.
 *  - Tile caching is owned by the browser + SDK; the app does not implement
 *    custom cache invalidation. Tiles are reused across route navigations
 *    inside the SPA because the `<script>` and HTTP tile responses stay in
 *    the browser cache (`Cache-Control` comes from Google's tile CDN).
 */
'use client';

import React, { useEffect, useRef, useMemo, useState } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { CITY_RECORDS } from '@loci/city-editions';

declare global {
  interface Window {
    /**
     * Google Maps JS calls this global if the Maps *authentication* check
     * fails. See `registerAuthFailureHandlerOnce` below.
     */
    gm_authFailure?: () => void;
  }
}

/** Charlotte, NC — the MVP market's centroid, read off its city record (COR-155). */
const DEFAULT_CENTER = {
  lat: CITY_RECORDS.CLT.geography.center.lat,
  lng: CITY_RECORDS.CLT.geography.center.lon,
} as const;
const DEFAULT_ZOOM = 13;

/**
 * Stable, greppable prefix for the Maps JS auth-failure log line (WEB-398).
 * Aggregate by this prefix in production logs to detect a misconfigured key.
 */
export const AUTH_FAILURE_LOG_PREFIX = '[GoogleMap][AUTH_FAILURE]';

// Google calls `window.gm_authFailure` when the Maps JS *authentication* check
// fails — a bad/expired key, an HTTP-referrer that isn't allowlisted, billing
// disabled, or the Maps JavaScript API not enabled on the project. This is a
// DIFFERENT failure mode from a hard script-load error: the `importLibrary()`
// promise still resolves and tiles can even render, so the `[SDK_LOAD_FAILED]`
// catch in the init effect never fires. Without this hook the only signal is
// Google's own greyed-out overlay + the "This page can't load Google Maps
// correctly" toast — invisible to our logs (WEB-398).
//
// IMPORTANT: this handler is observability ONLY. Defining `gm_authFailure` does
// NOT suppress Google's error overlay/toast — the SDK renders that
// independently — and we deliberately do not try to. Masking a genuine auth
// failure would ship a map that silently doesn't work. The honest fix is the
// API-key / referrer config documented in docs/security/google-maps-key-rotation.md
// §7; this prefix just makes the failure greppable in production logs.
let authFailureHandlerRegistered = false;
function registerAuthFailureHandlerOnce(): void {
  if (authFailureHandlerRegistered || typeof window === 'undefined') return;
  authFailureHandlerRegistered = true;
  // Chain any handler registered elsewhere so we never silently swallow it.
  const existing = window.gm_authFailure;
  window.gm_authFailure = () => {
    console.error(
      `${AUTH_FAILURE_LOG_PREFIX} Google Maps authentication failed — verify the API key's HTTP-referrer allowlist (must include app.loci.city), enabled APIs, and billing. See docs/security/google-maps-key-rotation.md §7.`,
    );
    if (typeof existing === 'function') existing();
  };
}

// Register at module import time (client only — guarded above). `MapCanvas` is
// dynamically imported only on map surfaces, so this never runs on non-map
// pages, and registering here guarantees the handler exists before the SDK's
// auth check runs.
registerAuthFailureHandlerOnce();

// The loader's setOptions() must only be called once per page load; subsequent
// calls are ignored and log a warning. Multiple MapCanvas instances (or effect
// re-runs) would otherwise spam the console.
let loaderInitialized = false;
function initLoaderOnce(apiKey: string): void {
  if (loaderInitialized) return;
  setOptions({
    key: apiKey,
    v: 'weekly',
    // `visualization` is intentionally omitted here so it's only fetched when
    // a surface actually calls `importLibrary('visualization')` (currently
    // only the FRE-757 heatmap effect in `GoogleMap.tsx`). Stories and
    // surfaces with `enableHeatmap={false}` therefore pay no extra cost.
    libraries: ['places', 'marker'],
  });
  loaderInitialized = true;
}

interface MapCanvasProps {
  apiKey: string;
  onMapLoad: (map: google.maps.Map) => void;
  center?: google.maps.LatLngLiteral;
  zoom?: number;
  className?: string;
  /** Cloud-based Google Maps mapId used for vector/2.5D styling. */
  mapId?: string;
  /** Tilt in degrees (0–45). Laptop: 30, desktop: 45. */
  tilt?: number;
  /** Max zoom level (e.g. 15 laptop, 16 desktop). */
  maxZoom?: number;
  /**
   * Local Google Maps JSON styles applied to the map instance (FRE-761).
   *
   * `styles` and `mapId` are mutually exclusive in the Maps SDK (cloud-based
   * `mapId` styling ignores raster JSON styles). As of WEB-397 an explicit
   * `mapId` prop takes precedence over `styles` — Advanced Markers require a
   * `mapId`, so any map rendering them must use cloud styling. Pass `styles`
   * only for local themes on maps that do **not** set a `mapId` (see
   * `apps/web/lib/map-styles/*`); use `mapId` for cloud-defined styles managed
   * in the Google Cloud console.
   *
   * IMPORTANT: pass a stable, module-level reference. A new reference on each
   * render will tear down and rebuild the map (dropping the tile cache and
   * flashing the skeleton) because this prop is a dependency of the map-init
   * effect. Prefer `styles={condition ? lociCltDark : undefined}` over
   * inline array literals.
   */
  styles?: google.maps.MapTypeStyle[];
  /**
   * Map color scheme for cloud-styled vector maps (`'LIGHT' | 'DARK' |
   * 'FOLLOW_SYSTEM'`). Unlike local JSON `styles`, this works alongside a
   * `mapId` — the Maps SDK applies the dark/light variant of the cloud Map
   * Style — so it's the mechanism a vector map (which requires a `mapId` for
   * Advanced Markers) uses to follow the app theme.
   *
   * IMPORTANT: `colorScheme` is an **initialization-only** Maps option — it
   * cannot be changed via `setOptions` after the map is created (see the SDK
   * `MapOptions.colorScheme` docs). A change therefore forces a full map
   * rebuild via the init effect, exactly like `styles`. Omit it (default) to
   * keep the SDK's legacy `LIGHT` default for surfaces that don't theme (e.g.
   * `/map`, `MapHero`).
   */
  colorScheme?: 'LIGHT' | 'DARK' | 'FOLLOW_SYSTEM';
  /**
   * Toggle the Maps SDK's built-in zoom-control widget. Defaults to `true`
   * to preserve historical behavior; surfaces that render their own zoom
   * chrome (e.g. the `/discover` map) pass `false`.
   *
   * @since WEB-72 (Discover · Tri-View Template — M4.4)
   */
  zoomControl?: boolean;
}

export const MapCanvas: React.FC<MapCanvasProps> = ({
  apiKey,
  onMapLoad,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  className = 'w-full h-full',
  mapId,
  tilt = 0,
  maxZoom,
  styles,
  colorScheme,
  zoomControl = true,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const onMapLoadRef = useRef(onMapLoad);
  onMapLoadRef.current = onMapLoad;
  // Tracks whether the SDK has instantiated the map so the container can
  // paint a skeleton placeholder instead of a blank white box. Flipping this
  // only after `new GoogleMapsMap(...)` resolves keeps the skeleton visible
  // across the async `importLibrary('maps')` fetch on slow connections.
  const [isMapReady, setIsMapReady] = useState(false);

  const stableCenter = useMemo(
    () => (center === DEFAULT_CENTER ? DEFAULT_CENTER : { ...center }),
    [center],
  );
  const stableZoom = zoom;
  const stableTilt = tilt;
  const stableMaxZoom = maxZoom;
  const stableZoomControl = zoomControl;

  useEffect(() => {
    let cancelled = false;

    const initMap = async () => {
      initLoaderOnce(apiKey);
      if (cancelled) return;

      const { Map: GoogleMapsMap } = (await importLibrary('maps')) as {
        Map: typeof google.maps.Map;
      };
      if (cancelled) return;

      if (mapRef.current && !googleMapRef.current) {
        // `mapId` and JSON `styles` are mutually exclusive — the Maps SDK
        // ignores `styles` whenever a `mapId` is present, and
        // `AdvancedMarkerElement` *requires* a `mapId`. Precedence (WEB-397):
        //   1. An explicit `mapId` prop always wins, so maps that render
        //      Advanced Markers (e.g. the discover map) attach their pins and
        //      let the cloud Map Style govern the basemap.
        //   2. Otherwise local JSON `styles` apply, for maps that theme
        //      locally without a `mapId` (e.g. the `/map` canvas).
        //   3. Otherwise fall back to the env Map ID so otherwise-unstyled
        //      maps (MapHero, VenueMiniMap) still get one.
        // (Avoid hardcoding the id — see NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID.)
        const hasLocalStyles = Array.isArray(styles) && styles.length > 0;
        const resolvedMapId =
          mapId ??
          (hasLocalStyles ? undefined : process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || undefined);
        const mapOptions: google.maps.MapOptions = {
          center: stableCenter,
          zoom: stableZoom,
          ...(resolvedMapId ? { mapId: resolvedMapId } : hasLocalStyles ? { styles } : {}),
          // `colorScheme` is honored on cloud-styled vector maps (i.e. with a
          // `mapId`) and is the theme-follow mechanism for the discover map.
          // Init-only — see the prop JSDoc + the dep array below.
          ...(colorScheme ? { colorScheme } : {}),
          disableDefaultUI: true,
          zoomControl: stableZoomControl,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          tilt: stableTilt,
          heading: 0,
          ...(stableMaxZoom != null ? { maxZoom: stableMaxZoom } : {}),
        };

        const map = new GoogleMapsMap(mapRef.current, mapOptions);
        if (cancelled) return;
        googleMapRef.current = map;
        setIsMapReady(true);
        onMapLoadRef.current(map);
      }
    };

    initMap().catch((err) => {
      // Tag with a stable prefix so operators can grep production logs after
      // a key rotation / quota event. The SDK also surfaces its own
      // `ExpiredKeyMapError` / `RefererNotAllowedMapError` chrome on the map
      // overlay — see docs/security/google-maps-key-rotation.md for triage steps.
      if (!cancelled) console.error('[GoogleMap][SDK_LOAD_FAILED]', err);
    });

    return () => {
      cancelled = true;
      const map = googleMapRef.current;
      if (typeof google !== 'undefined' && google?.maps?.event && map) {
        google.maps.event.clearInstanceListeners(map);
      }
      googleMapRef.current = null;
      setIsMapReady(false);
    };
    // stableTilt, stableMaxZoom, and stableZoomControl are intentionally
    // omitted — the map is created once with the *current* values, and the
    // second effect below reconciles any later changes via `map.setOptions`.
    // Including them here would tear down + recreate the map on every
    // toggle (dropping the tile cache and flashing the skeleton).
    //
    // The race window the linter is warning about — a caller flipping
    // `zoomControl` *during* the async `importLibrary('maps')` window — is
    // benign: the second effect runs after the map mounts and re-applies
    // the latest value via `map.setOptions({ zoomControl: ... })` in the
    // same render cycle.
    //
    // `styles` *does* participate so a caller-driven theme swap rebuilds
    // the map, but MUST be a stable reference (see prop JSDoc) — an inline
    // array would trigger a full teardown on every render.
    //
    // `colorScheme` likewise participates: it is an initialization-only Maps
    // option (cannot be updated via `setOptions`), so following the app's
    // light/dark theme requires recreating the map. It's a primitive string,
    // so referential stability is automatic.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
  }, [apiKey, stableCenter, stableZoom, mapId, styles, colorScheme]);

  // Sync center/zoom/tilt to existing map when props change after initial load
  useEffect(() => {
    const map = googleMapRef.current;
    if (map) {
      map.setCenter(stableCenter);
      map.setZoom(stableZoom);
      if (typeof map.setTilt === 'function') {
        map.setTilt(stableTilt);
      }
      if (typeof map.setOptions === 'function') {
        // Forward `undefined` so a transition back to a fast network clears
        // any cap previously set by `useAdaptiveMapZoom` — otherwise the
        // slow-network cap would persist for the rest of the session.
        map.setOptions({ maxZoom: stableMaxZoom, zoomControl: stableZoomControl });
      }
    }
  }, [stableCenter, stableZoom, stableTilt, stableMaxZoom, stableZoomControl]);

  return (
    <div className={`${className} relative`}>
      <div
        ref={mapRef}
        className="w-full h-full"
        aria-label="Interactive map showing event locations"
        role="application"
        tabIndex={0}
      />
      {!isMapReady && (
        <div
          className="absolute inset-0 bg-muted rounded animate-pulse flex items-center justify-center pointer-events-none"
          role="status"
          aria-label="Loading map"
          data-testid="map-loading-skeleton"
        >
          <span className="sr-only">Loading map...</span>
        </div>
      )}
    </div>
  );
};
